import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormBuilder, FormGroup, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs/internal/Subscription';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { DesignFactorsService } from '../../services/design-factors.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'app-design-factors',
  templateUrl: './design-factors.component.html',
  standalone: false
})
export class DesignFactorsComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _isOmvUser: boolean;
  private _designFactorsBackup: any;

  public designFactorsForm: FormGroup;
  public isLoading = signal<boolean>(true);
  public configMissing: boolean;
  public disabledMessage: string;

  public get connection(): FormGroup {
    return this.designFactorsForm.get("connection") as FormGroup;
  }

  public get pipeControls() {
    return this.designFactorsForm.controls;
  }

  public get connectionControls() {
    return this.connection.controls;
  }

  public control(name: string): UntypedFormControl {
    return this.designFactorsForm.get(name) as UntypedFormControl;
  }

  public connectionControl(name: string): UntypedFormControl {
    return this.connection.get(name) as UntypedFormControl;
  }

  @Input()
  private componentId: string;

  constructor(
    private _designFactorsService: DesignFactorsService,
    private _formBuilder: UntypedFormBuilder,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _messenger: MediatorService,
    private _store: StoreService
  ) {
    this.isLoading.set(true);
  }

  async ngOnInit() : Promise<void> {
    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    const user = await this._store.get<any>(StorageKeys.USER);
    this._isOmvUser = user?.profile?.organization == 'Omv'
    
    this.createDesignFactorsForm();

    this.getDesignFactors();
  }

  signalRfunc(data: { action: string, designId: string }) {
    if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS || data.action === PeriforOnChangeMessages.REFRESH_TUBULARS_LIST) {
      this.getDesignFactors();
    }
  }

  private getDesignFactors() {
    this._wellConfigService.getTubular().subscribe({
      next: (currentString) => {
        if (currentString?.id != undefined) {
          this._designFactorsService.getDesignFactors().subscribe(df => {
            this.designFactorsForm.patchValue(df, { emitEvent: false });
            this._designFactorsBackup = df;
            this.isLoading.set(false);
          });
        } else {
          this.configMissing = true;
          this.isLoading.set(false);
          this.disabledMessage = " Well Configuration Missing: Please specify at least one string";
        }
      }
    });
  }

  private createDesignFactorsForm(): void {
    this.designFactorsForm = this._formBuilder.group({
      apiCollapse: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
      barlowBurst: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
      pureCompression: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
      pureTension: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
      vonMisesBurst: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
      vonMisesCollapse: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
      connection: new UntypedFormGroup({
        burstLeak: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
        collapse: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
        compression: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
        tension: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
        envelopeBurst: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] }),
        envelopeCollapse: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0.1), Validators.max(10)] })
      })
    });

    this._subscriptions.add(this.designFactorsForm.valueChanges.subscribe((designFactors) => {
        this.setDefaultValues(designFactors);
        if (!this.isLoading() && this.designFactorsForm.valid) {
          if (!isEqual(designFactors, this._designFactorsBackup)) {
            this._designFactorsBackup = designFactors;
            this._designFactorsService.updateDesignFactors(designFactors).subscribe();
            this._messenger.publish(new DataSavedMessage('Design Factors'));
          }
        }
      })
    );
  }

  private setDefaultValues(df: any) {
    df.barlowBurst = df.barlowBurst || 1.1;
    df.apiCollapse = df.apiCollapse || (this._isOmvUser ? 1.1 : 1);
    df.pureTension = df.pureTension || 1.3;
    df.pureCompression = df.pureCompression || (this._isOmvUser ? 1.3 : 1.15);
    df.vonMisesBurst = df.vonMisesBurst || 1.1;
    df.vonMisesCollapse = df.vonMisesCollapse || 1;

    const con = df.connection;
    con.burstLeak = con.burstLeak || 1.1;
    con.collapse = con.collapse || (this._isOmvUser ? 1.1 : 1);
    con.compression = con.compression || (this._isOmvUser ? 1.3 : 1.15);
    con.tension = con.tension || 1.3;
    con.envelopeBurst = con.envelopeBurst || (this._isOmvUser ? 1.25 : 1.1);
    con.envelopeCollapse = con.envelopeCollapse || (this._isOmvUser ? 1.25 : 1);
    this.designFactorsForm.patchValue(df, {emitEvent: false});
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
